import Cookies from "js-cookie";
import { API_URL } from "../constants/api.const";
import { COOKIE_KEYS } from "@/shared/constants/cookies.const";
import { notifyAuthSessionChanged } from "./authSessionEvents";

type RefreshResponse = {
  accessToken?: string;
  code?: string;
  message?: string;
};

const REFRESH_FETCH_TIMEOUT_MS = 8_000;

let refreshInFlight: Promise<string> | null = null;
/** Server rejected the refresh cookie; stays set until a new access token is saved (login). */
let refreshRejected = false;

export function wasRefreshRejected(): boolean {
  return refreshRejected;
}

/** Secure flag for access cookie: HTTPS pages / production builds */
function isAccessCookieSecure(): boolean {
  if (typeof window !== "undefined" && window.location?.protocol === "https:") {
    return true;
  }
  return Boolean(import.meta.env.PROD);
}

export function saveAccessToken(token: string) {
  refreshRejected = false;
  Cookies.set(COOKIE_KEYS.token, token, {
    path: "/",
    sameSite: "lax",
    secure: isAccessCookieSecure(),
  });
  notifyAuthSessionChanged();
}

/** True when access JWT is present and not past exp (optional skew before expiry). */
export function isAccessTokenFresh(token: string, skewMs = 15_000): boolean {
  try {
    const payload = JSON.parse(atob(token.split(".")[1] || "")) as { exp?: number };
    return typeof payload.exp === "number" && payload.exp * 1000 > Date.now() + skewMs;
  } catch {
    return false;
  }
}

function clearLocalAccessToken() {
  const hadToken = Boolean(Cookies.get(COOKIE_KEYS.token));
  Cookies.remove(COOKIE_KEYS.token, { path: "/" });
  Cookies.remove(COOKIE_KEYS.refreshToken, { path: "/" });
  // Guests have nothing to clear; notifying would re-run every session listener for no change.
  if (hadToken) notifyAuthSessionChanged();
}

/**
 * Single-flight silent refresh via HttpOnly cookie.
 * Returns new access JWT; on failure clears client access token and throws.
 */
export async function ensureAccessViaRefresh(): Promise<string> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REFRESH_FETCH_TIMEOUT_MS);
    let res: Response;
    try {
      res = await fetch(`${API_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: "{}",
        signal: controller.signal,
      });
    } catch (err) {
      const aborted = err instanceof Error && err.name === "AbortError";
      const e = new Error(aborted ? "Session refresh timed out" : "Session refresh failed");
      (e as Error & { code?: string }).code = aborted ? "REFRESH_TIMEOUT" : "REFRESH_NETWORK";
      throw e;
    } finally {
      clearTimeout(timer);
    }

    let body: RefreshResponse = {};
    try {
      body = (await res.json()) as RefreshResponse;
    } catch {
      /* ignore parse errors */
    }

    if (res.status === 503) {
      const err = new Error(body.message || "Session store unavailable");
      (err as Error & { code?: string }).code = body.code || "REFRESH_UNAVAILABLE";
      // Do not clear local access on 503 — avoids logout storm when Redis is briefly down
      throw err;
    }

    if (!res.ok || !body.accessToken) {
      refreshRejected = true;
      clearLocalAccessToken();
      const err = new Error(body.message || "Session expired. Please sign in again.");
      (err as Error & { code?: string }).code = body.code || "REFRESH_INVALID";
      throw err;
    }

    saveAccessToken(body.accessToken);
    return body.accessToken;
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

/** Best-effort server logout (revokes Redis session + clears HttpOnly cookie). */
export async function portalServerLogout(): Promise<void> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5_000);
    try {
      await fetch(`${API_URL}/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: "{}",
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timer);
    }
  } catch {
    /* ignore network errors — client still clears local state */
  }
}

/**
 * True when tRPC HTTP response is an auth failure that can be fixed by refresh.
 */
export function isTrpcUnauthorizedResponse(res: Response, bodyText: string): boolean {
  if (res.status === 401) return true;
  if (res.status !== 200 && res.status !== 207) return false;
  try {
    const parsed = JSON.parse(bodyText) as unknown;
    if (!parsed || typeof parsed !== "object") return false;

    const checkError = (err: unknown): boolean => {
      if (!err || typeof err !== "object") return false;
      const e = err as {
        data?: { code?: string; httpStatus?: number; message?: string };
        json?: { data?: { code?: string; httpStatus?: number }; message?: string; code?: number };
        code?: string | number;
        message?: string;
      };
      const code = e.data?.code || e.json?.data?.code || (typeof e.code === "string" ? e.code : undefined);
      if (code === "UNAUTHORIZED") return true;
      const httpStatus = e.data?.httpStatus ?? e.json?.data?.httpStatus;
      if (httpStatus === 401) return true;
      return false;
    };

    if ("error" in (parsed as object) && checkError((parsed as { error: unknown }).error)) {
      return true;
    }

    if (Array.isArray(parsed)) {
      return parsed.some((item) => item && typeof item === "object" && "error" in item && checkError(item.error));
    }
  } catch {
    return false;
  }
  return false;
}
